import { EnvironmentInjector, inject, Injectable } from '@angular/core';
import { TasksListApiService } from '@simOn/ticket/element/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { TaskPlacementDraft } from 'feature-task';
import { firstValueFrom, merge, take } from 'rxjs';
import {
  AgentActionOutcome,
  CreateTicketActionDto,
  UpdateTicketActionDto,
  UpdateTicketStatusActionDto
} from '../model';
import { AgentPanelHostService } from './agent-panel-host.service';
import { TicketDraftSource, toTicketChecklistSourceId, toTicketFormDraft } from './agent-ticket-draft.mappers';

// eslint-disable-next-line @nx/enforce-module-boundaries
type FeatureTaskChunk = typeof import('feature-task');
type TaskPanel = InstanceType<FeatureTaskChunk['TaskPanelService']>;

/**
 * Handles the `CreateTicket`, `UpdateTicket` and `UpdateTicketStatus` draft cards. Nothing is saved by the agent:
 * the regular task form opens in the right sidenav prefilled from the card (through the same draft hand-off the
 * form uses when it comes back from the 3D walk) and the user reviews and saves it as usual. A status change
 * needs no form: the card button is the confirmation and the regular `UpdateTicketStatus` request runs.
 *
 * For an update the CURRENT ticket is loaded first and the card's changes are applied on top, so the form (and
 * the PUT body it builds) keeps attachments, position and everything the card does not mention.
 *
 * The form lives in `feature-task`, which a feature library may not depend on statically; it is loaded lazily
 * like the ticket preview.
 */
@Injectable({ providedIn: 'root' })
export class AgentTicketDraftService {
  private readonly _host = inject(AgentPanelHostService);
  private readonly _tasksApi = inject(TasksListApiService);
  private _featureTaskChunk?: Promise<FeatureTaskChunk>;
  private _panelInjector?: EnvironmentInjector;
  /** Settles the outcome of the card whose panel is still open when another card replaces it. */
  private _dismissPending?: () => void;

  async create(action: CreateTicketActionDto): Promise<AgentActionOutcome> {
    const panel = await this._openPanel(action.apartmentId);
    const outcome = this._outcomeOf(panel);
    panel.openAdd(action.apartmentId, this._draft(action, { payload: action.payload }));
    return outcome;
  }

  async update(action: UpdateTicketActionDto): Promise<AgentActionOutcome> {
    const current = await firstValueFrom(this._tasksApi.getTicket(action.targetId, action.apartmentId));
    const panel = await this._openPanel(action.apartmentId);
    const outcome = this._outcomeOf(panel);
    const draft = this._draft(action, { payload: action.payload, clearedFields: action.clearedFields, current });
    // `openEdit` loads the ticket again for its permission check and the form's baseline; the draft lands on top.
    // The author id feeds that check (an author may edit their own ticket), exactly like the tasks list passes it.
    panel.openEdit(action.targetId, action.apartmentId, current.author?.userId ?? null, undefined, draft);
    return outcome;
  }

  async updateStatus(action: UpdateTicketStatusActionDto): Promise<AgentActionOutcome> {
    const { ticketId, status } = action.payload;
    await firstValueFrom(this._tasksApi.updateTicketStatus({ ticketId, status }, action.apartmentId));
    return 'saved';
  }

  private async _openPanel(apartmentId: string): Promise<TaskPanel> {
    const portal = await this._host.portalFor(apartmentId, ['/space', apartmentId, 'dashboard', 'tasks']);
    if (!portal) throw new Error('[ai-agent] no panel host for the task form');

    // eslint-disable-next-line @nx/enforce-module-boundaries
    const { TaskPanelService, TaskAccessService } = await (this._featureTaskChunk ??= import('feature-task'));
    // The services are scoped to a page component elsewhere; one instance per opened panel keeps that lifetime.
    this._dismissPending?.();
    this._panelInjector?.destroy();
    this._panelInjector = this._host.createPanelInjector(portal, [TaskPanelService, TaskAccessService]);
    return this._panelInjector.get(TaskPanelService);
  }

  /** `saved` once the panel reports a created or modified task; `dismissed` when another card replaces the panel. */
  private _outcomeOf(panel: TaskPanel): Promise<AgentActionOutcome> {
    return new Promise<AgentActionOutcome>((resolve) => {
      const saved = merge(panel.refresh$, panel.taskModified$)
        .pipe(take(1))
        .subscribe(() => resolve('saved'));
      this._dismissPending = (): void => {
        saved.unsubscribe();
        resolve('dismissed');
      };
    });
  }

  private _draft(action: CreateTicketActionDto | UpdateTicketActionDto, source: TicketDraftSource): TaskPlacementDraft {
    return {
      apartmentId: action.apartmentId,
      spaceName: action.apartmentName ?? '',
      formValue: toTicketFormDraft(source),
      checklistSourceId: toTicketChecklistSourceId(source),
      tempMarkerId: crypto.randomUUID(),
      positionDeleted: false,
      attachments: [],
      isDirty: false
    };
  }
}
